import { useCallback, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

const defaultSearchData = {
  search: '',
  fullName: undefined,
  companyName: undefined,
  communicationMethod: undefined,
  leadNumber: undefined,
  workflowStatusId: undefined,
  leadSourceId: undefined,
  productId: undefined,
  offerId: undefined,
  priority: undefined,
  departmentId: undefined,
  branchId: undefined,
  teamId: undefined,
  appointmentId: undefined,
  assigneeUuid: undefined,
  districtId: undefined,
  cityId: undefined,
  administrativeRegionId: undefined,
  withAppointment: undefined,
  createdDateFrom: undefined,
  createdDateTo: undefined,
  activityDateFrom: undefined,
  activityDateTo: undefined,
  assignDateFrom: undefined,
  assignDateTo: undefined,
  sortOrder: 'DESC',
};

const useSearchData = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getString = (key, fallback = '') => searchParams.get(key) ?? fallback;
  const getBoolean = (key, fallback) => {
    const val = searchParams.get(key);
    return val !== null ? val === 'true' : fallback;
  };

  const searchData = useMemo(
    () => ({
      search: getString('search', defaultSearchData.search),
      fullName: getString('fullName') || undefined,
      companyName: getString('companyName') || undefined,
      communicationMethod: getString('communicationMethod') || undefined,
      leadNumber: getString('leadNumber') || undefined,
      unassignedOnly: getBoolean('unassignedOnly', defaultSearchData.unassignedOnly),
      workflowStatusId: getString('workflowStatusId') || undefined,
      leadSourceId: getString('leadSourceId') || undefined,
      productId: getString('productId') || undefined,
      offerId: getString('offerId') || undefined,
      priority: getString('priority') || undefined,
      departmentId: getString('departmentId') || undefined,
      branchId: getString('branchId') || undefined,
      teamId: getString('teamId') || undefined,
      appointmentId: getString('appointmentId') || undefined,
      assigneeUuid: getString('assigneeUuid') || undefined,
      districtId: getString('districtId') || undefined,
      cityId: getString('cityId') || undefined,
      administrativeRegionId: getString('administrativeRegionId') || undefined,
      withAppointment: getBoolean('withAppointment', defaultSearchData.withAppointment),
      createdDateFrom: getString('createdDateFrom') || undefined,
      createdDateTo: getString('createdDateTo') || undefined,
      activityDateFrom: getString('activityDateFrom') || undefined,
      activityDateTo: getString('activityDateTo') || undefined,
      assignDateFrom: getString('assignDateFrom') || undefined,
      assignDateTo: getString('assignDateTo') || undefined,
      sortOrder: getString('sortOrder', defaultSearchData.sortOrder),
    }),
    [searchParams]
  );

  const setSearchData = useCallback(
    (updatedOrFn) => {
      const updated =
        typeof updatedOrFn === 'function' ? updatedOrFn(searchData) : updatedOrFn;
      const nextParams = new URLSearchParams();
      const fullSearchData = { ...searchData, ...updated };

      Object.entries(fullSearchData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          nextParams.set(key, String(value));
        }
      });

      setSearchParams(nextParams, { replace: true });
    },
    [searchData, setSearchParams]
  );

  const resetSearchData = useCallback(() => {
    const nextParams = new URLSearchParams();
    Object.entries(defaultSearchData).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        nextParams.set(key, String(value));
      }
    });
    setSearchParams(nextParams, { replace: true });
  }, [setSearchParams]);

  return {
    searchData,
    setSearchData,
    resetSearchData,
    defaultSearchData,
  };
};

export default useSearchData;
